import { useStudies } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { WithApolloContext } from 'modules/Apollo/types';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags/suspense';
import { useMemo } from 'react';

/**
 * The `cases` variable for `GET_HANGING_PROTOCOL`: one group per case, the case being read first, then
 * the priors in the order their `comparisonIndex` was assigned in, then anything else.
 *
 * **The backend validates this, and a malformed value fails the whole request.** `HangRequest` treats
 * each group as one case and rejects two shapes:
 *
 * - a group whose studies span more than one case -- _"Found multiple cases {…} for a set of studies
 *   […] meant to represent a single case"_
 * - a group that names a case but omits some of its studies -- _"Found extra study-IDs {…} for a case
 *   (worklist-item ID: …) that was specified as a list of study-IDs […]"_
 *
 * The rejection surfaces as `hangingProtocol` resolving to null, which used to read as "nothing to
 * apply" -- so creating a protocol left the viewer on the Default HP with the new protocol's steps
 * still in the step bar (VX-4905).
 *
 * ## Why prior grouping stays behind the flag
 *
 * `comparisonIndex` and `numberOfPriors` are written at save time by `useGetComparisonIndex` and
 * `useNumberOfPriors`, **both of which branch on `FF.EXAM_GROUPED_HANGING_PROTOCOLS`**: exam-grouped
 * when it is on, one index and one count per prior *study* when it is off. Those values are what a
 * viewport's prior slot is resolved through, so the grouping here has to agree with the grouping that
 * produced them. Grouping priors by exam while the saved protocol counts them per study renumbers the
 * prior slots underneath it, and the request then resolves -- to the *wrong prior*. A viewport quietly
 * showing the wrong comparison study is worse than one that fails to resolve, so this follows the flag
 * rather than getting ahead of it.
 *
 * Consequence worth stating plainly: with the flag **off**, two loaded priors from one exam still
 * produce a group per study, and the backend still rejects it. Flag-off cannot express that case at
 * all. Making it work means migrating `useGetComparisonIndex` / `useNumberOfPriors` too -- which is
 * what turning the flag on does -- and until then the rejection is at least reported rather than
 * swallowed (see `useSelectedHangingProtocol.ts`).
 *
 * ## What is fixed unconditionally
 *
 * `mainStudiesIds` is `worklist.studies` **plus `worklist.linkedStudies`**, and linked studies belong
 * to other worklist items, so sending it as one group spans several cases on any case that has one.
 * That is wrong under either treatment and is fixed for both. Linked studies are not priors --
 * `priorStudiesIds` excludes everything in `mainStudiesIds` -- so they carry no `comparisonIndex`, and
 * their groups go **last** for that reason: appending keeps every prior at the position it already had,
 * where inserting them ahead of the priors would shift the very indices this function must not disturb.
 *
 * One case this cannot fix from here: if the viewer has loaded only some of an order's studies, the
 * group is genuinely incomplete and the backend is right to reject it. The client does not know an
 * order's full membership, so that needs the request to name cases by worklist-item ID instead of by
 * study IDs.
 *
 * Example with the flag on -- a case with two studies, one linked study from another order, and two
 * priors sharing a third order:
 *   [['case-a-1', 'case-a-2'], ['prior-c-1', 'prior-c-2'], ['linked-b-1']]
 */
export function useHangingProtocolCaseGroups({ context }: WithApolloContext): string[][] {
  const enableExamGrouping = useFeatureFlagEnabled(FF.EXAM_GROUPED_HANGING_PROTOCOLS);
  const { studies, mainStudiesIds, linkedStudiesIds, priorStudiesIds } = useStudies({ context });

  return useMemo(() => {
    if (mainStudiesIds.length === 0) return [];

    const orderBySmid = new Map(studies.map((study) => [study.smid, study.hl7Order]));
    const linked = new Set(linkedStudiesIds);

    // The case being read: every main study that is not on loan from another order. `hl7Order` is
    // deliberately not consulted -- these studies are this case by definition, and grouping them by
    // order would split the primary case if it ever spans two.
    const primary = mainStudiesIds.filter((smid) => !linked.has(smid));
    const claimed = new Set(primary);

    // Groups a list by the order each study belongs to, preserving first-seen order so the result is
    // stable across renders. That matters: this value is a query variable, and a new identity refetches.
    //
    // `splitOrderless` decides what happens to a study whose `hl7Order` is missing -- which arrives as
    // `''`, not null, since the field is a required string. The two callers need opposite answers, and
    // the reason is the same one that keeps prior grouping behind the flag at all:
    //
    // - **Priors: false.** `useGetComparisonIndex` and `useNumberOfPriors` group exam-grouped priors by
    //   the raw `hl7Order`, so two orderless priors share one `comparisonIndex` and count as one prior.
    //   Splitting them here would send two prior groups for one saved slot, and the protocol would
    //   resolve against the wrong comparison study. Mirroring the save side keeps the slots aligned,
    //   even though coalescing unrelated studies is the shape the backend can reject -- a rejection is
    //   visible, a silently wrong prior is not.
    // - **Linked studies: true.** They carry no `comparisonIndex` at all, so nothing downstream can be
    //   misaligned by splitting them, and lumping unrelated linked studies into one group is only the
    //   rejectable shape with no upside.
    const groupByOrder = (
      smids: readonly string[],
      { splitOrderless }: { splitOrderless: boolean }
    ): string[][] => {
      const groups = new Map<string, string[]>();
      for (const smid of smids) {
        if (claimed.has(smid)) continue;
        claimed.add(smid);
        const order = orderBySmid.get(smid) ?? '';
        const key = splitOrderless && order === '' ? `__no-order:${smid}` : order;
        const group = groups.get(key);
        if (group == null) groups.set(key, [smid]);
        else group.push(smid);
      }
      return [...groups.values()];
    };

    // One group per prior study when the flag is off, matching the per-study `comparisonIndex` and
    // `numberOfPriors` that path saves. `claimed` still guards against a prior that is also a main
    // study, which would otherwise name the same case twice.
    const oneGroupPerPrior = (): string[][] => {
      const groups: string[][] = [];
      for (const smid of priorStudiesIds) {
        if (claimed.has(smid)) continue;
        claimed.add(smid);
        groups.push([smid]);
      }
      return groups;
    };

    const priorGroups = enableExamGrouping
      ? groupByOrder(priorStudiesIds, { splitOrderless: false })
      : oneGroupPerPrior();

    return [
      primary,
      ...priorGroups,
      ...groupByOrder(linkedStudiesIds, { splitOrderless: true }),
    ].filter((group) => group.length > 0);
  }, [enableExamGrouping, mainStudiesIds, linkedStudiesIds, priorStudiesIds, studies]);
}
